import { Routes, Route, Navigate, useLocation, useNavigationType } from 'react-router-dom'
import { lazy, Suspense, useEffect, useLayoutEffect, useRef } from 'react'
// The Photographer Studio is a separate chunk so public DG routes do not load it.
const FoundGuestPage = lazy(() => import('./studio/pages/FoundGuestPage'))
const StudioApp = lazy(() => import('./studio/StudioApp'))
// The public beta invitation lives outside the Studio session (and its auth guard).
const InvitationPage = lazy(() => import('./studio/pages/invitation/InvitationPage'))

export default function App() {
  const location = useLocation()
  const navigationType = useNavigationType()
  const previousPathname = useRef<string | null>(null)

  useLayoutEffect(() => {
    const previous = previousPathname.current
    previousPathname.current = location.pathname

    if (previous === null || previous === location.pathname || navigationType === 'POP') return

    if (location.hash) {
      let id = location.hash.slice(1)
      try {
        id = decodeURIComponent(id)
      } catch {
        // Keep the raw fragment if it contains malformed percent-encoding.
      }
      const anchor = document.getElementById(id)
      if (anchor) {
        anchor.scrollIntoView()
        return
      }
    }

    window.scrollTo(0, 0)
  }, [location.pathname, location.hash, navigationType])

  const { pathname } = location

  useEffect(() => {
    const isPrivateArea =
      pathname === '/admin' || pathname.startsWith('/admin/') || pathname === '/studio' || pathname.startsWith('/studio/') || pathname.startsWith('/invitacion/')
    if (!isPrivateArea) return

    const existingRobotsMeta = document.head.querySelector('meta[name="robots"]')
    const robotsMeta = existingRobotsMeta ?? document.createElement('meta')
    const previousContent = robotsMeta.getAttribute('content')

    robotsMeta.setAttribute('name', 'robots')
    robotsMeta.setAttribute('content', 'noindex, nofollow')
    if (!existingRobotsMeta) document.head.appendChild(robotsMeta)

    return () => {
      if (previousContent === null) {
        robotsMeta.remove()
      } else {
        robotsMeta.setAttribute('content', previousContent)
      }
    }
  }, [pathname])

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/studio/eventos" replace />} />
      <Route path="/invitacion/:token" element={<Suspense fallback={null}><InvitationPage /></Suspense>} />
      <Route path="/e/:slug" element={<Suspense fallback={null}><FoundGuestPage /></Suspense>} />
      <Route
        path="/studio/*"
        element={
          <Suspense fallback={null}>
            <StudioApp />
          </Suspense>
        }
      />
      {/* Redirect legacy */}
      <Route path="/fotos" element={<Navigate to="/studio/eventos" replace />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  )
}

function NotFound() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-parchment-100 px-6">
      <div className="text-center">
        <p className="font-display text-8xl text-parchment-400 mb-4">404</p>
        <h1 className="font-display text-2xl text-ink-700 mb-2">Página no encontrada</h1>
        <p className="font-body text-sm text-ink-400 mb-8">
          La página que buscás no existe o fue movida.
        </p>
        <a href="/" className="btn-primary">Volver al inicio</a>
      </div>
    </div>
  )
}
